import {
  AfterViewInit,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
  Validators,
  ValidationErrors,
  Validator,
  NG_VALIDATORS
} from '@angular/forms';
import { NgxMaterialIntlTelInputComponent } from 'ngx-material-intl-tel-input';
import { Subject, distinctUntilChanged, takeUntil } from 'rxjs';
import { AssetsService } from '../../../core/services/assets.service';

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.html',
  standalone: false,
  styleUrls: ['./phone-input.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInput),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInput),
      multi: true
    }
  ]
})
export class PhoneInput implements ControlValueAccessor, OnInit, AfterViewInit, OnChanges, OnDestroy, Validator {

  @ViewChild(NgxMaterialIntlTelInputComponent)
  private telephoneInput?: NgxMaterialIntlTelInputComponent;

  @Input() defaultCountry: string = 'tz';
  @Input() countryId: string = '';
  @Input() required: boolean = true;
  @Input() disabled: boolean = false;
  @Input() appearance: 'fill' | 'outline' = 'fill';
  @Input() autoIpLookup: boolean = false;
  @Input() autoSelectCountry: boolean = true;
  @Input() autoSelectedCountry: string = '';
  @Input() numberValidation: boolean = true;
  @Input() phoneValidation: boolean = true;
  @Input() enableSearch: boolean = true;
  @Input() includeDialCode: boolean = false;
  @Input() emojiFlags: boolean = false;
  @Input() preferredCountries: string[] = [];
  @Input() visibleCountries: string[] = [];
  @Input() excludedCountries: string[] = [];
  @Input() enablePlaceholder: boolean = true;
  @Input() initialValue: string = '';
  @Input() useMask: boolean = false;
  @Input() forceSelectedCountryCode: boolean = false;
  @Input() showMaskPlaceholder: boolean = false;
  @Input() enableInputMaxLength: boolean = true;
  @Input() localizeCountryNames: boolean = false;
  @Input() mainLabel: string = '';
  @Input() inputClass: string = '';
  @Input() textLabels: any = {
    mainLabel: '',
    codePlaceholder: '',
    searchPlaceholderLabel: '',
    noEntriesFoundLabel: '',
    nationalNumberLabel: '',
    hintLabel: '',
    invalidNumberError: '',
    requiredError: ''
  };

  innerControl = new FormControl('');
  resolvedCountryCode = '';
  private countrySyncTimer: ReturnType<typeof setTimeout> | null = null;
  private countrySyncSequence = 0;
  private readonly destroyed = new Subject<void>();

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private readonly assets: AssetsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['required']) {
      this.updateValidators();
    }
    if (changes['countryId']) {
      this.scheduleCountrySync();
    }
  }

  ngOnInit() {
    this.updateValidators();

    this.innerControl.valueChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe(value => this.onChange(value));

    this.innerControl.statusChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed))
      .subscribe(() => this.onValidatorChange?.());
  }

  ngOnDestroy(): void {
    if (this.countrySyncTimer) {
      clearTimeout(this.countrySyncTimer);
      this.countrySyncTimer = null;
    }
    this.destroyed.next();
    this.destroyed.complete();
  }

  private async syncCountry(countryId: string, sequence: number): Promise<void> {
    try {
      const country = countryId
        ? await this.assets.getCountry(countryId)
        : null;
      if (sequence !== this.countrySyncSequence || countryId !== this.countryId) return;
      const code = String(
        country?.metadata?.['iso2']
        || country?.code
        || this.autoSelectedCountry
        || this.defaultCountry,
      ).trim().toLowerCase();
      if (code) {
        this.resolvedCountryCode = country ? code : '';
        this.selectTelephoneCountry(code);
      }
    } catch {
      // Keep the configured default if geography cannot be loaded.
    }
  }

  private selectTelephoneCountry(code: string): void {
    const input = this.telephoneInput;
    const country = input?.allCountries?.find((option) => option.iso2 === code);
    if (!input || !country || input.prefixCtrl.value?.iso2 === code) return;
    const numberControl = input.telForm.get('numberControl');
    const number = numberControl?.value;
    // The telephone library feeds prefix changes back through its full form and
    // field control. Suppress that feedback here: the prefix control still
    // updates the visible flag/dial code, while avoiding a recursive value loop.
    input.prefixCtrl.setValue(country, { emitEvent: false });
    // Re-emit only the national number once so the library formats the existing
    // value with the newly selected dial code.
    if (number) numberControl?.setValue(number);
  }

  private scheduleCountrySync(): void {
    const sequence = ++this.countrySyncSequence;
    const countryId = this.countryId;
    if (this.countrySyncTimer) {
      clearTimeout(this.countrySyncTimer);
    }

    this.countrySyncTimer = setTimeout(() => {
      this.countrySyncTimer = null;
      void this.syncCountry(countryId, sequence);
    }, 0);
  }

  private updateValidators(): void {
    const validators = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    this.innerControl.setValidators(validators);
    this.innerControl.updateValueAndValidity({ emitEvent: false });
  }

  private onValidatorChange: (() => void) | null = null;

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  onPhoneValueChange(value: any) {
    this.innerControl.setValue(value);
  }

  onBlur() {
    this.onTouched();
  }

  ngAfterViewInit() {
    this.scheduleCountrySync();
  }

  writeValue(value: any): void {
    this.innerControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled && this.innerControl.enabled) {
      this.innerControl.disable({ emitEvent: false });
    } else if (!isDisabled && this.innerControl.disabled) {
      this.innerControl.enable({ emitEvent: false });
    }
  }

  validate(): ValidationErrors | null {
    if (this.innerControl.errors) {
      const errors: any = {};

      // Map ngx-material-intl-tel-input errors to custom messages
      if (this.innerControl.errors['required']) {
        errors['required'] = 'Phone number is required';
      }

      // Only validate phone number format if phoneValidation is enabled
      if (this.phoneValidation && (this.innerControl.errors['invalidNumber'] || this.innerControl.errors['validatePhoneNumber'])) {
        errors['invalid'] = 'Please enter a valid phone number';
      }

      // If no specific errors were mapped, provide a generic message (only if validation is enabled)
      if (this.phoneValidation && Object.keys(errors).length === 0) {
        errors['invalid'] = 'Please enter a valid phone number';
      }

      return errors;
    }
    return null;
  }

  getErrors(): any {
    return this.innerControl.errors;
  }

  isValid(): boolean {
    return this.innerControl.valid;
  }

  isTouched(): boolean {
    return this.innerControl.touched;
  }
}
